import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  AlertCircle,
  AlignCenter,
  Info,
  RefreshCcw,
  Search,
} from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { Pagination } from "semantic-ui-react";
import Dropdown from "../components/input/Dropdown";
import { formatFromJsonDate } from "../dateConfig";
import { notificationActions } from "../store/notification/notification-slice";
import ReadMoreText from "./ReadMoreText";
import Input from "./input/Input";

const ViewAllNotifications = forwardRef((props, ref) => {
  const notifications = useSelector((state) => state.notification.list);
  const user = useSelector((state) => state.user);
  const [currentPage, setCurrentPage] = useState(1);
  const notificationsPerPage = 10;
  const dispatch = useDispatch();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [notificationType, setNotificationType] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
   const { closeModal } = props;

  useImperativeHandle(ref, () => ({
    resetFilters: () => {
      setFromDate("");
      setToDate("");
      setNotificationType([]);
      setFilteredNotifications(notifications);
    },
  }));

  useEffect(() => {
    setFilteredNotifications(notifications);
  }, [notifications]);

  const filterNotifications = (e) => {
    e.preventDefault();
    let filtered = notifications;

    if (notificationType.length > 0) {
      filtered = filtered.filter((notification) =>
        notificationType.includes(notification.notType),
      );
    }

    if (fromDate) {
      filtered = filtered.filter(
        (notification) => new Date(notification.notDate) >= new Date(fromDate),
      );
    }

    if (toDate) {
      filtered = filtered.filter(
        (notification) => new Date(notification.notDate) <= new Date(toDate),
      );
    }

    setFilteredNotifications(filtered);
  };

  const resetFilters = () => {
    setFromDate("");
    setToDate("");
    setNotificationType([]);
    setFilteredNotifications(notifications);
  };

  // Function to remove a notification
  const removeNotification = (e, index) => {
    // Prevent link from navigating to href
    e.preventDefault();

    dispatch(notificationActions.removeNotification(index));

    // Update local storage
    dispatch(notificationActions.addToLocalStorage());
  };

  // Pagination change handler
  const onPageChange = (e, pageInfo) => {
    setCurrentPage(pageInfo.activePage);
  };

  // Determine the number of total pages
  const totalPages = Math.ceil(
    filteredNotifications.length / notificationsPerPage,
  );

  // Determine the notifications to display on the current page
  const indexOfLastNotification = currentPage * notificationsPerPage;
  const indexOfFirstNotification =
    indexOfLastNotification - notificationsPerPage;
  const currentNotifications = filteredNotifications.slice(
    indexOfFirstNotification,
    indexOfLastNotification,
  );

  const toggleSidebar = () => {
    setIsSidebarVisible((prev) => !prev);
  };

  const notificationTypeOptions = [
    { key: "Info", text: "Info", value: "Info" },
    { key: "Warning", text: "Warning", value: "Warning" },
    { key: "Error", text: "Error", value: "Error" },
  ];

  return (
    <div
      className="ui segment fullscreen-component"
      style={{ position: "relative" }}
    >
      <div className="fullscreen-header">
        <div className="fullscreen-tittle-toggle">
          <div onClick={toggleSidebar}>
            <AlignCenter size={20} />
          </div>
          <div>
            <h4 className="ui header">Notification Center</h4>
          </div>
        </div>
        <div className="fullscreen-pagination">
          {totalPages > 1 && (
            <Pagination
              defaultActivePage={1}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          )}
        </div>
      </div>
      <div className="fullscreen-filter-main-content">
        <form
          className={`ui form segment ${
            isSidebarVisible ? "with-filter" : "without-filter"
          }`}
        >
          <div className="one-field">
            <Dropdown
              label="Notification Type"
              multiple={true}
              options={notificationTypeOptions}
              placeholder="Select Features"
              value={notificationType}
              onChange={(e, { value }) => setNotificationType(value)}
            />
          </div>
          <div className="one-field">
            <Input
              label="From Date"
              type="date"
              name="from_date"
              placeholder="From Date"
              max={new Date().toISOString().split("T")[0]}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="one-field">
            <Input
              label="To Date"
              type="date"
              name="to_date"
              placeholder="To Date"
              min={fromDate}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <button
              type="submit"
              className="ui primary button filter-btn"
              onClick={filterNotifications}
            >
              <Search size={15} style={{ marginRight: "5px" }} />
              Filter
            </button>
            <button
              type="button"
              className="ui secondary button reset-btn"
              onClick={resetFilters}
            >
              <RefreshCcw size={15} style={{ marginRight: "5px" }} />
              Reset
            </button>
          </div>
        </form>
        <div
          className="ui segment fullscreen-main-content"
          style={{ width: isSidebarVisible ? "80vw" : "100%" }}
        >
          <div className="fullscreen-dropdown-mid">
            {currentNotifications.length > 0 ? (
              currentNotifications.map((notification, index) => (
                <div
                  className={`notification-item ${
                    notification.notType === "Warn" ||
                    notification.notType === "Warning"
                      ? "warn-notification"
                      : notification.notType === "Error"
                        ? "error-notification"
                        : "info-notification"
                  }`}
                  key={index}
                >
                  <div className="notification-icon">
                    {notification.notType === "Warn" ||
                    notification.notType === "Warning" ? (
                      <AlertCircle size={25} color="#FF8C00" />
                    ) : notification.notType === "Error" ? (
                      <AlertCircle size={25} color="red" />
                    ) : (
                      <Info size={25} color="green" />
                    )}
                  </div>
                  <div className="notification-content">
                    <div className="notification-type">
                      {notification.notType}
                    </div>
                    <div>
                      <ReadMoreText
                        text={notification.notTitle}
                        maxLength={isSidebarVisible ? 100 : 100}
                      />
                    </div>
                  
                    <div className="fullscreen-time-author">
                      <div>{formatFromJsonDate(notification.notDate)}</div>
                      <div>{notification.notUser}</div>
                    </div>
                      {(notification.notEventType === 31 ||
                      notification.notEventType === 33) && (
                      <div style={{ marginTop: "6px" }}>
                        <button
                          className="ui tiny primary button"
                          onClick={(e) => {
                            e.stopPropagation();
                            props.navigate(notification.notUrl);
                             closeModal();
                          }}
                        >
                          Download
                        </button>
                      </div>
                    )}
                  </div>
                  {/* <div
                    onClick={(e) => removeNotification(e, index, notification.notId)}
                    className="notification-close"
                  >
                    <X size={15} color="#8C8997" />
                  </div>
                  <div className="notification-dot"></div> */}
                </div>
              ))
            ) : (
              <center className="no-notification">
                No notifications available
              </center>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default ViewAllNotifications;
